import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { saleAPI } from "../../services/saleAPI";

export type CancelSaleDialogProps = {
  open: boolean;
  sale: {
    id: string;
    saleNumber?: string;
    netAmount: number;
    status?: string;
  } | null;
  onClose: () => void;
  onSuccess: () => void;
};

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export default function CancelSaleDialog({
  open,
  sale,
  onClose,
  onSuccess,
}: CancelSaleDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open, sale?.id]);

  if (!sale) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setError("Please enter a cancel reason (min 3 characters)");
      return;
    }
    setBusy(true);
    try {
      await saleAPI.cancelSale(sale!.id, trimmedReason);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Cancel failed"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Cancel sale</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {sale.saleNumber || sale.id} · {formatCurrency(sale.netAmount)}
            </Typography>
            <Alert severity="warning">
              Sale remains in records as cancelled. It will not count in sales
              totals; wallet and stock are restored where applicable.
            </Alert>
            <TextField
              label="Cancel reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
              disabled={busy}
              inputProps={{ maxLength: 500 }}
              placeholder="e.g. Wrong bill, duplicate entry"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={busy}>
            {busy ? "Cancelling…" : "Confirm cancel"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
